import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, Subscription, firstValueFrom } from 'rxjs';
import { combineLatestWith, filter, map, take } from 'rxjs/operators';

import {
  AuthUserStore,
  UserState,
} from '@arc/arc-shared-frontend';
import { NavigationEnd, Router } from '@angular/router';
import { TenantService } from '../services/tenant.service';
import { Title } from '@angular/platform-browser';

@Component({
  selector: 'application-navigation',
  templateUrl: './application-navigation.container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApplicationNavigationContainerComponent implements OnInit, OnDestroy {
  @Output() profileView = new EventEmitter();
  @Output() tenantSwitchToggle = new EventEmitter();
  @Output() logout = new EventEmitter();

  userStore$: Observable<UserState>;
  tenantName$: Observable<string>;
  userName$: Observable<string> | any;
  resellerId$: Observable<string>;
  application$: Observable<string>;

  routerSubscription: Subscription;

  constructor(
    private store: Store<any>,
    private tenantService: TenantService,
    private titleService: Title,
    private router: Router,
  ) {}

  async ngOnInit() {
    this.userStore$ = this.store.pipe(select(AuthUserStore));

    // TODO: make these selectors in the AuthUserStore
    this.userName$ = this.userStore$.pipe(
      map(
        userStore =>
          `${userStore.user.firstName || ''} ${userStore.user.lastName || ''}`
      )
    );

    this.tenantName$ = this.userStore$.pipe(
      map(userStore => userStore?.activeTenant?.tenantName ? userStore.activeTenant.tenantName: '')
    );

    this.resellerId$ = this.tenantService.resellerId$;

    this.tenantService.resellerId$?.pipe(
      take(1)
    ).subscribe(resellerId => {
      this.setTitleHeader([this.applicationName(location.pathname), resellerId])
    });


    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map((event: NavigationEnd) => this.applicationName(event.url)),
      combineLatestWith(this.resellerId$)
    ).subscribe(this.setTitleHeader);
  }

// TODO: replace with takeUntilDestroyed after ng17 upgrade
  ngOnDestroy() {
    this.routerSubscription.unsubscribe();
  }

  // TODO: remove the event chain when the tenant switcher is modified
  onTenantSwitching() {
    this.tenantSwitchToggle.emit();
  }

  onLogout() {
    this.logout.emit();
  }

  onProfileView() {
    this.profileView.emit();
  }

  setTitleHeader = ([application, resellerId]) => {
    if (application === 'Login') {
      return;
    }

    this.titleService.setTitle(`${application} | ${this.tenantService.resellerName(resellerId)}`);
    const link: HTMLLinkElement = document?.querySelector("link[rel='icon']");
    if (link) {
      switch (resellerId?.toLowerCase()) {
        case 'jpmorgan':
          link.href = 'assets/layout/images/jpm-favicon.ico?v1';
          break;
        default:
          link.href = 'assets/layout/images/favicon.ico?v1';
          break;   
      }
    }
  }

  applicationName(url: string) {
    // exceptions to our naming convention
    if (url.includes('payment-accounts')) {
      return 'Accounts';
    }

    if (url.includes('credit-admin')) {
      return 'System Credit';
    }

    if (url.includes('users/roles')) {
      return 'Roles';
    }

    let name = url
      .split('/')[1]
      .toLowerCase()
      .split('-')[0]
      .split('?')[0];

    switch (name) {
      case 'cash':
        name = 'Cash App';
        break;
      case 'bpn':
        name = 'Payments';
        break;
      case 'gpt':
        name = 'Finance Copilot';
        break;
      case 'profile':
        name = 'My Profile';
        break;
      default:
        name = `${name.charAt(0).toUpperCase()}${name.slice(1)}`
        break;
    }
      return name;
  }
}
